import { NotifType } from "./storeApp";
import { defineStore } from "pinia";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { displayError } from "~/utils/errors";
import { useStoreApp } from "~/stores/storeApp";
import mitt from "mitt";
import { usePrimaryMarketplace } from "~/stores/storePrimaryMarketplace";
import { AuctionStatus } from "~/common/enums/auction";
import { useStoreFlashSale } from "~/stores/storeFlashSale";
import { useSecondaryMarketplace } from "~/stores/storeSecondaryMarketplace";
import { useStoreDeck } from "~/stores/storeDeck";
import { RemovableRef, useLocalStorage } from "@vueuse/core";
import { auctionApi } from "~/api/auction";
import { gameApi } from "~/api/game/game";
import { maticMinAmount } from "~/application/config";
import {
  NftType,
  AuctionUpdate,
  MarketType,
  GameweekType,
  GameweekStatus,
  NftSkill,
  NftEquipment,
  SocketEventType,
  StripeBidError,
} from "fungi-types";
import { useTracking } from "~/composables/useGtm";
import i18n from "~/modules/i18n";
import { GameweeksList } from "~/api/game/types";
import socketService from "~/services/socketService";
import { useWeb3AuthStore } from "./storeWeb3auth";
import useConvert from "~/composables/useConvert";
import { OrderWithCounter } from "@opensea/seaport-js/lib/types";
const { maticToWei } = useConvert;

export type StoreMarketplaceEvent = {
  "update-auction": AuctionUpdate;
};

export const useStoreMarketplace = defineStore("marketplace-store", {
  state: () => {
    return {
      initialized: false,
      bidInProgress: false,
      emitter: mitt<StoreMarketplaceEvent>(),
      selectedItem: {} as NftSkill | NftEquipment,
      type: useLocalStorage("MarketNftType", NftType.SKILL) as RemovableRef<NftType>,
      onGoingGameweeks: [] as GameweeksList["results"],
    };
  },
  actions: {
    async init() {
      const storeFlashSale = useStoreFlashSale();

      if (this.initialized) return;

      socketService.on(SocketEventType.AUCTION_UPDATED, async (event) => {
        this.updateAuctionHandler(event);
        if (this.selectedItem.auction?.cuid === event.auction.cuid) {
          if (this.selectedItem.auction.marketType === MarketType.PRIMARY) {
            const job = await usePrimaryMarketplace().updateAuction(event, [this.selectedItem]);
            this.selectedItem = job?.item as NftSkill | NftEquipment;
          } else {
            const job = await useStoreFlashSale().updateAuction(event, [this.selectedItem]);
            this.selectedItem = job?.item as NftSkill | NftEquipment;
          }
        }
      });

      socketService.on(SocketEventType.CANT_BID_WITH_STRIPE, (event) => {
        this.cantBidWithStripeHandler(event);
      });

      this.getOngoingGameweeks();

      this.initialized = true;
      try {
        await storeFlashSale.getFlashSale();
      } catch (error) {}
    },

    async cantBidWithStripeHandler(data: StripeBidError) {
      const userId = useStoreUserV2().cuid;

      if (data.userCuid !== userId) return;

      useStoreApp().displayNotification(NotifType.BID_FAILED, data.auction);
    },

    async getOngoingGameweeks() {
      const resp = await gameApi.gameweek.list.query({
        status: [GameweekStatus.Upcoming],
        type: GameweekType.PRO,
        limit: 20,
      });
      this.onGoingGameweeks = resp.results;
    },

    async updateAuctionHandler(data: AuctionUpdate) {
      const storeFlashSale = useStoreFlashSale();
      const secondaryMarketplace = useSecondaryMarketplace();
      const storeDeck = useStoreDeck();

      let updated;
      if ([MarketType.PRIMARY, MarketType.PRIMARY_EQUIPMENT].includes(data.auction.marketType)) {
        updated = await usePrimaryMarketplace().updateAuction(data);
      } else if (
        [MarketType.FLASH_SALE, MarketType.FLASH_SALE_EQUIPMENT].includes(data.auction.marketType)
      ) {
        updated = await storeFlashSale.updateAuction(data);
      } else if (
        [MarketType.SECONDARY, MarketType.SECONDARY_EQUIPMENT].includes(data.auction.marketType)
      ) {
        secondaryMarketplace.updateAuction(data);
        if (data.auction.status === AuctionStatus.UNLISTED) {
          secondaryMarketplace.removeCard(data.auction.cuid);
        }
        if (data.auction.card?.tokenId) {
          storeDeck.updateItemAuction(data.auction.card.tokenId, { status: data.auction.status });
        }
      }

      if (updated) {
        if (data.auction.status === AuctionStatus.CANCELLED) {
          this.emitter.emit("update-auction", data);
          return;
        }

        const userCuid = useStoreUserV2().cuid;

        const end = new Date(data.auction.endedAt);

        const isAuctionEnded = end.getTime() <= new Date().getTime();

        // check if the auction is ended
        if (!isAuctionEnded && userCuid && updated.prevBidder?.cuid !== data.bidder.cuid) {
          if (
            userCuid === data.bidder.cuid &&
            (updated.prevBidder?.cuid !== userCuid || userCuid === data.bidder.cuid)
          ) {
            useStoreApp().displayNotification(NotifType.TOPBID, updated.item as NftSkill);
          }
        } else if (!isAuctionEnded && userCuid && userCuid === data.bidder.cuid) {
          // same bidder
          useStoreApp().displayNotification(NotifType.OVERBID, updated.item as NftSkill);
        }

        this.emitter.emit("update-auction", data);
      }
    },

    async bidOnCard({
      item,
      bidAmountWpol,
      bidAMountUsdc,
      creditAmount,
      finalAmount = 0,
    }: {
      item: NftSkill | NftEquipment;
      bidAmountWpol: number;
      bidAMountUsdc?: number;
      creditAmount?: number;
      finalAmount: number;
    }): Promise<any> {
      const storeUser = useStoreUserV2();
      const web3authStore = useWeb3AuthStore();
      const gtm = useTracking();
      const auction = item.auction;
      const { t } = i18n.global;
      const bidAmountWei = maticToWei(bidAmountWpol);

      if (!auction) {
        throw new Error("Item does not have an auction");
      }

      this.bidInProgress = true;
      try {
        // 1. Check wallet is connected
        if (!web3authStore.isConnected) {
          throw new Error("Wallet not connected");
        }

        // 2. Check if user has enough POL for the gas fees
        if (Number(web3authStore.polBalance) < maticMinAmount && finalAmount > 0) {
          this.bidInProgress = false;
          return Promise.reject(Error(t("_payment.insufficient-pol-gas-fees")));
        }

        // 4. Create bid order (using Seaport SDK + ethers)
        if (!web3authStore.seaportService) {
          console.info("Seaport service not available");
          throw new Error("Seaport service not available");
        }

        let offerer = storeUser.hasSmartWallet
          ? storeUser.smartWalletAddress
          : storeUser.walletAddress;

        console.log("offerer address for bid:", offerer);
        const resp = await auctionApi.orders.primary.orderParams.query({
          auctionCuid: auction.cuid,
          address: offerer,
          amountUsd: bidAMountUsdc,
          amount: bidAmountWei.toString(),
          credits: creditAmount?.toString() || "0",
        });

        let order: OrderWithCounter;

        if (storeUser.hasSmartWallet) {
          order = await web3authStore.seaportSmartWalletService!.getOrderSignature(
            resp.orderParams,
            offerer
          );
        } else {
          order = await web3authStore.seaportService.getOrderSignature(resp.orderParams, offerer);
        }

        await auctionApi.bid.place.mutate({
          signature: order.signature,
          parameters: order.parameters,
          cuid: auction.cuid,
          orderToken: resp.orderToken,
        });

        this.bidInProgress = false;
        gtm.trackSuccessfulBid({
          value: bidAmountWei,
          auction_id: auction.cuid,
          token_id: item.tokenId,
          currency: "POL",
          username: storeUser.username,
          user_id: storeUser.cuid,
          market:
            auction.marketType === MarketType.SECONDARY ? MarketType.SECONDARY : MarketType.PRIMARY,
          nft_type: item.cardType,
          payment_method: "web3",
        });
        storeUser.refreshBalance();
        return Promise.resolve();
      } catch (error) {
        this.bidInProgress = false;
        displayError(error);
        return Promise.reject(error);
      }
    },
  },
});
